import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import _, { set } from "lodash";
import { headers, actionOptions, servicesDummyData } from "./service-list-helper";
import { useLocation, useNavigate } from "react-router-dom";
import { UseSelector } from "react-redux";
import ProfileHeader from "../../../../../common/components/profile-header/profile-header-and-breadcrumbs";
import { routes } from "../../../../../constants/routes";
import { useSetLoading } from "../../../../../common/hooks/appearance";
import { AlertComponent } from "../../../../../common/components";
import CreateService from "../create-service/create-service";
import ServiceListSearchAndFilters from "./service-list-search-and-filters";
import "./services-list.scss";
import { getServices, deleteService } from "../../../../../api/admin/service-management";
import ListingTable from "../../../../../common/components/listing-table/list-table";
import AdminPaginationComponent from "../../../../../common/components/admin-pagination-component/admin-pagination-component";
import RequestStatus from "../../../../../common/components/request-status/request-status";
import ApproveOrRejectRequestPopup from "../../provider-management/provider-requests/approve-or-reject-popup";
import ArchiveIconWithBg from "../../../../../assets/icons/archiveWithBg.svg";
import { formatCurrency } from "../create-service/create-service-helper";
import type { FilterValues } from "./filter";


const ServiceList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const breadcrumbLinkForList = [
    {
      label: "Service Management",
      link: routes.SERVICE_LIST,
      handleClick: () => { },
      disablePointerEvents: true,
    },
    {
      label: "Service List",
      link: "#",
      handleClick: () => { },
    },
  ];
  const [breadcrumbLink, setBreadcrumbLink] = useState(breadcrumbLinkForList);
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const [error, setError] = useState("");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState<number>(20);
  const [isOpenFilter, setIsOpenFilter] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<any>(
    location?.state?.searchText || ""
  );
 const [activeFilters, setActiveFilters] = useState<FilterValues>(
  location?.state?.activeFilters || {
    serviceName: "",
    serviceCode: "",
    categoryIds: [],
    minPrice: "",
    maxPrice: "",
    status: [], // <-- array
  }
);

  const EMPTY_FILTERS: FilterValues = {
    serviceName: "",
    serviceCode: "",
    categoryIds: [],
    minPrice: "",
    maxPrice: "",
    status: [],
  };

  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isResetSearch, setIsResetSearch] = useState(false);
  const searchRef = useRef<{ clear: () => void }>(null);
  const isSearchTriggered = useRef(false);
  const pageSize = 10;
  const [isCreateServiceOpen, setIsCreateServiceOpen] = useState(false);
  const [actionType, setActionType] = useState<"create" | "edit" | "view" | "archive">("create");
  const [currentServiceId, setCurrentServiceId] = useState<number | null>(null);
  const [isDeleteServiceOpen, setIsDeleteServiceOpen] = useState(false);


 const fetchServices = async (page: number, search: string, filters: FilterValues) => {
  setLoading(true);
  setError("");
  try {
    const offset = (page - 1) * pageSize;

    const params: any = {
      page,
      limit: pageSize,
      offset,
      search: search || "",
    };

    // ✅ filter params
    if (filters.serviceName?.trim()) params.serviceName = filters.serviceName.trim();
    if (filters.serviceCode?.trim()) params.serviceCode = filters.serviceCode.trim();

    if (Array.isArray(filters.categoryIds) && filters.categoryIds.length > 0)
      params.categoryIds = filters.categoryIds; // <-- array

    if (String(filters.minPrice || "").trim() !== "") params.minPrice = filters.minPrice;
    if (String(filters.maxPrice || "").trim() !== "") params.maxPrice = filters.maxPrice;

    if (Array.isArray(filters.status) && filters.status.length > 0)
      params.status = filters.status; // <-- array (["active","inactive"])

    const response = await getServices(params);

    if (response?.status === 200) {
      setData(response?.data?.data?.data || []);
      setTotalRecords(response?.data?.data?.meta?.totalCount || 0);
    }
  } catch (err: any) {
    setError(
      err.response?.data?.message || "Failed to fetch services. Please try again."
    );
  } finally {
    setLoading(false);
  }
};


  const handleSearch = useCallback(
    _.debounce((text: string) => {
      isSearchTriggered.current = true;
      setCurrentPage(1); // Reset to page 1 on search
      setSearchText(text);
      if (text === "") {
        setIsResetSearch(true);
        setTimeout(() => setIsResetSearch(false), 100);
      }
    }, 500),
    []
  );

  const resetSearch = () => {
    setSearchText("");
    if (searchRef.current) {
      searchRef.current.clear();
    }
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };

const appliedFilterCount = useMemo(() => {
  let count = 0;

  if (activeFilters.serviceName?.trim()) count += 1;
  if (activeFilters.serviceCode?.trim()) count += 1;

  if (Array.isArray(activeFilters.categoryIds) && activeFilters.categoryIds.length > 0)
    count += 1;

  const minOk = String(activeFilters.minPrice || "").trim() !== "";
  const maxOk = String(activeFilters.maxPrice || "").trim() !== "";
  if (minOk || maxOk) count += 1;

  if (Array.isArray(activeFilters.status) && activeFilters.status.length > 0)
    count += 1;

  return count;
}, [activeFilters]);


  useEffect(() => {
    const pageToUse = isSearchTriggered.current ? 1 : currentPage;
    fetchServices(pageToUse, searchText || "", activeFilters);
    isSearchTriggered.current = false;
  }, [currentPage, searchText, activeFilters]);

  const formattedData = useMemo(() => {
    if (Array.isArray(data)) {
      return data.map((item: any) => {
        return {
          id: item.id,
          serviceName: item.name,
          serviceCode: item.code,
          category: item.categories?.map((cat: any) => cat.name).join(", ") || "-",
          basePrice: `${item.basePrice?.min ? `$${formatCurrency(item.basePrice?.min)}` : "$0"}`,
          status: item.status,
        };
      });
    }
    return [];
  }, [data]);

  const noResultsTxt = useMemo(() => {
    if (loading) {
      return "Loading services...";
    }
    if (searchText && appliedFilterCount === 0) {
      return "No services found matching your search criteria.";
    }
    if (!searchText && appliedFilterCount > 0) {
      return "No services found matching your filtered criteria.";
    }
    if (searchText && appliedFilterCount > 0) {
      return "No services found for search and filter criteria.";
    }
    return "No services found";
  }, [loading, searchText, appliedFilterCount]);

  const handleRowClick = (serviceId: number) => {
    setCurrentServiceId(serviceId);
    setActionType("view");
    setIsCreateServiceOpen(true);
    // setIsViewOpen(true);
  };

  const handleAction = (actionLabel: string, serviceId: number) => {
    if (actionLabel === "Edit") {
      setCurrentServiceId(serviceId);
      setActionType("edit");
      setIsCreateServiceOpen(true);
    }
    else if (actionLabel === "Archive") {
      setCurrentServiceId(serviceId);
      setActionType("archive");
      setIsDeleteServiceOpen(true);
    }
    // Handle other actions like "archive" here
  };

  const fetchServicesWithCurrentParams = () => {
    if(currentPage === 1) {
      fetchServices(1, searchText, activeFilters);
      return;
    }
    else {
      setCurrentPage(1);
    }
  }

  const deleteServiceAPI  = async (id: number) => {
    setLoading(true);
    try {
      const response = await deleteService(id);
      if (response?.status === 200) {
        setAlertMessage("Service archived successfully.");
        setAlertType("success");
        setAlertOpen(true);
        // Refresh the services list
        fetchServicesWithCurrentParams();
      }
    } catch (error:any) {
      setAlertMessage(error.response?.data?.message || "Failed to archive service. Please try again.");
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  }

  const handleDeleteService = () => { 
    setIsDeleteServiceOpen(false);
    // API call to delete service with currentServiceId
    deleteServiceAPI(currentServiceId!);
  }

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <div className="service-main-wrapper listing-page-main-wrapper">
      <div style={{
        display: isCreateServiceOpen ? "flex" : "none",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden"
      }}
      >
        <CreateService
          setIsCreateServiceOpen={setIsCreateServiceOpen}
          setAlertMessageForListPage={setAlertMessage}
          setIsAlertPopupOpenForListPage={setAlertOpen}
          setAlertTypeForListPage={setAlertType}
          setLoadingForListPage={setLoading}
          actionType={actionType}
          fetchServices={() => {
            setCurrentPage(1);
            fetchServices(1, searchText || "", EMPTY_FILTERS);
          }}
          serviceId={currentServiceId}
          setCurrentServiceId={setCurrentServiceId}
          isCreateServiceOpen={isCreateServiceOpen}
          setActionType={setActionType}
        />
      </div>
      <div style={{
        display: isCreateServiceOpen ? "none" : "flex",
        flexDirection: "column",
        height: "100%",
      }}>
        <ProfileHeader breadcrumbLink={breadcrumbLink} />
        <div
          className="list-and-filter-wrapper service-list-and-filter-wrapper"
          style={{
            display: !isViewOpen ? "flex" : "none",
            flexDirection: "column",
            height: "100%",
            paddingBottom: totalRecords > 0 ? "0px" : "10px",
          }}
        >
          <div className="list-search-and-filters service-list-search-and-filters">
            <ServiceListSearchAndFilters
              onSearch={handleSearch}
              onFilterChange={(filters) => {
                setActiveFilters(filters);
                setCurrentPage(1);
              }}
              onResetFilters={() => {
                setActiveFilters({
                  serviceName: "",
                  serviceCode: "",
                  categoryIds: [],
                  minPrice: "",
                  maxPrice: "",
                  status: [],
                });
                setCurrentPage(1);
              }}
              filterValues={activeFilters}
              appliedFiltersCount={appliedFilterCount}
              isFilterOpen={isOpenFilter}
              setIsFilterOpen={setIsOpenFilter}
              searchRef={searchRef}
              isReset={isResetSearch}
              localSearchText={searchText}
              placeholder="Search service by name, code, or category"
              createServiceHandler={() => {
                setIsCreateServiceOpen(true);
                setActionType("create");
              }}
            />

          </div>
          <div className="listing-table-wrapper service-listing-table-wrapper">
            <ListingTable
              headers={headers}
              data={formattedData?.map((item) => ({
                ...item,
                status: (
                  <RequestStatus
                    status={
                      item.status
                        ? item.status.charAt(0).toUpperCase() +
                        item.status.slice(1)
                        : ""
                    }
                  />
                ),
                action: "verticalEllipse",
                actionOptions: actionOptions,
              }))}
              onRowClick={(row: any) => {
                handleRowClick(row?.id);
              }}
              noResultsText={noResultsTxt}
              fieldName={"services-list"}
              handleAction={handleAction}
            />
            <ApproveOrRejectRequestPopup
              open={isDeleteServiceOpen}
              title="Archive Service"
              note="Are you sure you want to archive this service? Archived services will no longer be visible or selectable to providers."
              buttonText="Continue"
              cancelBtnText="Cancel"
              onOkay={() => {
                handleDeleteService();
              }}
              onCancel={() => {
                setIsDeleteServiceOpen(false);  
              }}
              fieldName="approve"
              iconSrc={ArchiveIconWithBg}
            />
          </div>
          {totalRecords > 0 && (
            <AdminPaginationComponent
              totalRecords={totalRecords}
              pageSize={pageSize}
              currentPage={currentPage}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      </div>

      <AlertComponent
        message={alertMessage}
        type={alertType ?? "info"}
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
      />
    </div>
  );
};

export default ServiceList;
